// Work Projects Domain - Professional development and work-related conversations
export const workProjectsData = {
  hub: {
    id: "hub_work_projects",
    label: "Work Projects",
    content: "Professional development and work-related conversations including SaaS development, AI integration, and technical challenges",
    visibility: "public",
    timestamp: 1704067200000,
    category: "Domain Hub",
    level: 0,
    nodeType: "hub"
  },
  
  clusters: [
    {
      id: "cluster_saas_platform",
      label: "SaaS Platform Development",
      content: "Building and scaling the main product platform - architecture decisions, performance optimization, and user experience",
      visibility: "private",
      timestamp: 1704024000000,
      category: "Project Cluster",
      level: 1,
      parentHub: "hub_work_projects",
      nodeType: "cluster"
    },
    {
      id: "cluster_ai_integration",
      label: "AI Integration Project",
      content: "Implementing LLM features and AI-powered functionality - exploring costs, security, and technical implementation",
      visibility: "ask me",
      timestamp: 1703980800000,
      category: "Project Cluster",
      level: 1,
      parentHub: "hub_work_projects",
      nodeType: "cluster"
    }
  ],

  areas: [
    {
      id: "area_backend_architecture",
      label: "Backend Architecture",
      content: "Database design, API architecture, and scalability planning for enterprise-grade applications",
      visibility: "private",
      timestamp: 1704019200000,
      category: "Topic Area",
      level: 2,
      parentCluster: "cluster_saas_platform",
      parentHub: "hub_work_projects",
      nodeType: "area"
    },
    {
      id: "area_frontend_optimization",
      label: "Frontend Performance",
      content: "React optimization, state management, and user experience improvements",
      visibility: "public",
      timestamp: 1704009600000,
      category: "Topic Area",
      level: 2,
      parentCluster: "cluster_saas_platform",
      parentHub: "hub_work_projects",
      nodeType: "area"
    },
    {
      id: "area_llm_integration",
      label: "LLM Integration",
      content: "Language model APIs, prompt engineering, and AI workflows with security considerations",
      visibility: "ask me",
      timestamp: 1703976000000,
      category: "Topic Area",
      level: 2,
      parentCluster: "cluster_ai_integration",
      parentHub: "hub_work_projects",
      nodeType: "area"
    },
    {
      id: "area_vector_databases",
      label: "Vector Databases",
      content: "Embeddings, similarity search, and semantic retrieval systems for AI applications",
      visibility: "public",
      timestamp: 1703966400000,
      category: "Topic Area",
      level: 2,
      parentCluster: "cluster_ai_integration",
      parentHub: "hub_work_projects",
      nodeType: "area"
    }
  ],

  mementos: [
    {
      id: "memento_001",
      label: "Database Schema: User permissions system",
      content: "Me: 'I'm designing a user permissions system for our multi-tenant SaaS. How should I structure the database to handle roles and permissions efficiently?' \n\nDiscussion covered RBAC implementation, role hierarchy design, and database normalization. Key insight: use junction tables for many-to-many relationships between users, roles, and permissions. Decided on: users -> user_roles -> roles -> role_permissions -> permissions structure. Also discussed permission inheritance and caching strategies for performance.",
      visibility: "private",
      timestamp: 1704016800000,
      category: "Architecture Discussion",
      level: 3,
      parentArea: "area_backend_architecture",
      parentCluster: "cluster_saas_platform",
      parentHub: "hub_work_projects",
      subcategory: "Database Design",
      nodeType: "memento"
    },
    {
      id: "memento_002",
      label: "API Rate Limiting: Preventing abuse while maintaining UX",
      content: "Me: 'Our API is getting hammered by some users. What's the best way to implement rate limiting without affecting legitimate users?' \n\nExplored different algorithms: token bucket, sliding window, fixed window. Discussed Redis-based implementation and graceful degradation strategies. Decided on sliding window counter with 1000 requests/hour per user, 10,000/hour per organization. Also implemented burst allowance and proper error messages with retry-after headers.",
      visibility: "public",
      timestamp: 1704013200000,
      category: "Technical Implementation",
      level: 3,
      parentArea: "area_backend_architecture",
      parentCluster: "cluster_saas_platform",
      parentHub: "hub_work_projects",
      subcategory: "API Design",
      nodeType: "memento"
    },
    {
      id: "memento_003",
      label: "React Performance: Dashboard rendering optimization",
      content: "Me: 'Our dashboard with 500+ data points is taking 3+ seconds to render. Users are complaining about the lag. Help me optimize this.' \n\nAnalyzed component re-render issues. Implemented React.memo for expensive chart components and useMemo for costly calculations. Used virtualization for large lists and lazy loading for charts. Result: reduced render time from 3.2s to 0.8s. Key insight: memoize props objects and use callback refs for dynamic content.",
      visibility: "public",
      timestamp: 1704006600000,
      category: "Performance Analysis",
      level: 3,
      parentArea: "area_frontend_optimization",
      parentCluster: "cluster_saas_platform",
      parentHub: "hub_work_projects",
      subcategory: "React Optimization",
      nodeType: "memento"
    },
    {
      id: "memento_004",
      label: "State Management: Zustand vs Redux for our app",
      content: "Me: 'Our React app is getting complex. Should we stick with useState/useContext or move to Redux? What about Zustand?' \n\nCompared state management solutions: Redux (mature, devtools, middleware), Zustand (simpler, smaller bundle), Context (built-in, limited). For our MVP: chose Zustand for simplicity and performance. Redux would be overkill for current needs but planned migration path if we need time-travel debugging or complex middleware later.",
      visibility: "private",
      timestamp: 1704000000000,
      category: "Architecture Decision",
      level: 3,
      parentArea: "area_frontend_optimization",
      parentCluster: "cluster_saas_platform",
      parentHub: "hub_work_projects",
      subcategory: "State Management",
      nodeType: "memento"
    },
    {
      id: "memento_005",
      label: "OpenAI API: Massive cost reduction strategy",
      content: "Me: 'Our OpenAI bill hit $2,400 last month! We need to cut costs without degrading the user experience. Any ideas?' \n\nImplemented comprehensive cost optimization: prompt compression techniques, semantic caching with embeddings, response streaming, and token counting. Used system prompts to reduce per-request tokens and implemented 24-hour cache for similar queries. Result: 65% cost reduction while maintaining quality. Monthly bill now ~$850.",
      visibility: "ask me",
      timestamp: 1703971200000,
      category: "Cost Optimization",
      level: 3,
      parentArea: "area_llm_integration",
      parentCluster: "cluster_ai_integration",
      parentHub: "hub_work_projects",
      subcategory: "API Optimization",
      nodeType: "memento"
    },
    {
      id: "memento_006",
      label: "Prompt Injection: Security audit findings",
      content: "Me: 'Security team found our LLM feature might be vulnerable to prompt injection. How serious is this and how do we fix it?' \n\nCRITICAL security issue identified. Users could manipulate system prompts to extract sensitive data or bypass content filters. Implemented: input sanitization, output validation, separate system/user contexts, and sandboxing. Added monitoring for suspicious patterns. NEVER trust user input directly in prompts - always validate and sanitize.",
      visibility: "ask me",
      timestamp: 1703964000000,
      category: "Security Review",
      level: 3,
      parentArea: "area_llm_integration",
      parentCluster: "cluster_ai_integration",
      parentHub: "hub_work_projects",
      subcategory: "Security",
      nodeType: "memento"
    },
    {
      id: "memento_007",
      label: "Vector Database: Pinecone vs Chroma evaluation",
      content: "Me: 'We need a vector database for semantic search. Pinecone looks good but expensive. Chroma is open-source. Which should we choose?' \n\nDetailed comparison: Pinecone (managed, fast, expensive), Chroma (open-source, self-hosted, good performance). Benchmarked: Pinecone 20ms avg query, Chroma 45ms. Decision: Pinecone for production (reliability critical), Chroma for development/staging. Cost vs performance trade-off worth it for production workloads.",
      visibility: "public",
      timestamp: 1703961600000,
      category: "Technology Evaluation",
      level: 3,
      parentArea: "area_vector_databases",
      parentCluster: "cluster_ai_integration",
      parentHub: "hub_work_projects",
      subcategory: "Database Selection",
      nodeType: "memento"
    },
    {
      id: "memento_008",
      label: "Embedding Models: Cost vs quality analysis",
      content: "Me: 'OpenAI embeddings are good but pricey at scale. What are our alternatives for generating embeddings?' \n\nCompared options: OpenAI ada-002 ($0.0001/1K tokens, excellent quality), sentence-transformers (free, good quality, requires hosting), Cohere (mid-range pricing). Hybrid approach: OpenAI for user-facing features where quality matters, sentence-transformers for internal/bulk processing. Saves ~40% on embedding costs.",
      visibility: "public",
      timestamp: 1703956800000,
      category: "Technology Comparison",
      level: 3,
      parentArea: "area_vector_databases",
      parentCluster: "cluster_ai_integration",
      parentHub: "hub_work_projects",
      subcategory: "ML Models",
      nodeType: "memento"
    }
  ]
};
